import classNames from "classnames";
import { graphql } from "gatsby";
import React, { ReactNode } from "react";
import AnchorButton from "../../components/AnchorButton/AnchorButton";
import Breadcrumbs, {
  Breadcrumb
} from "../../components/Breadcrumbs/Breadcrumbs";
import Card from "../../components/Card/Card";
import { Col, Row, Wrapper } from "../../components/Grid";
import Image, { ImageRatios } from "../../components/Image/Image";
import SocialLinks from "../../components/SocialLinks/SocialLinks";
import { FormattedMessage } from "react-intl";
import { SanityPage } from "../../model/common";
import {
  SanityLesson,
  SanityUnitOfWork,
  textIdsForUnit
} from "../../model/lessons";
import { urlForDocument } from "../../urls";
import { findLessonNumber } from "../../utils/downloads";
import { imageUrlBuilder } from "../../utils/images";
import RelatedUnitLessonContent from "../RelatedUnitLessonContent/RelatedUnitLessonContent";
import Text from "../Text/Text";
import UnitOfWorkSummary from "../UnitOfWorkSummary/UnitOfWorkSummary";
import styles from "./UnitOfWorkLayout.module.scss";

export const unitOfWorkRelatedField = graphql`
  fragment SanityUnitOfWorkRelatedField on SanityUnitOfWork {
    related {
      ... on SanityDocument {
        _id
        _type
      }
      ... on SanityMake {
        title
        description
        image {
          ...SanityImage
        }
        slug {
          current
        }
      }
      ... on SanityUnitOfWork {
        title
        description
        ageRange
        unitType
        image {
          ...SanityImage
        }
        contents {
          _id
        }
        slug {
          current
        }
      }
      ... on SanityIndividualLesson {
        title
        description
        image {
          ...SanityImage
        }
        slug {
          current
        }
      }
      ... on SanityPdCourse {
        title
        description
        image {
          ...SanityImage
        }
        slug {
          current
        }
      }
    }
  }
`;

interface UnitOfWorkLayoutProps {
  listings: SanityPage;
  lesson?: SanityLesson;
  unit: SanityUnitOfWork;
  details: ReactNode;
}

const UnitOfWorkLayout = ({
  listings,
  unit,
  lesson,
  details
}: UnitOfWorkLayoutProps) => {
  const { title } = lesson ?? unit;
  const breadcrumbs: Breadcrumb[] = [
    {
      href: urlForDocument(listings),
      title: listings.title
    },
    {
      href: urlForDocument(unit),
      title: unit.title
    }
  ];
  if (lesson) {
    breadcrumbs.push({
      href: urlForDocument(lesson),
      title: lesson.title
    });
  }

  const lessonIndex = !lesson ? -1 : findLessonNumber(unit, lesson) - 1;
  const nextLesson = unit.contents[lessonIndex + 1];
  const nextLessonUrl = nextLesson ? urlForDocument(nextLesson) : undefined;
  return (
    <Wrapper className={styles.root}>
      <Row justify="center">
        <Col md={10} sm={12} className={styles.preamble}>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
          <SocialLinks title={title} />
        </Col>
      </Row>
      <Row justify="center">
        <Col md={10} sm={12}>
          <Card type="main" className={styles.card}>
            {lesson && lesson.image ? (
              <Image
                className={classNames(styles.image, styles.lessonImage)}
                image={lesson.image}
                widths={400}
                aspectRatio={ImageRatios.Landscape_4_3}
                corners="rounded"
              />
            ) : (
              <img
                className={classNames(styles.image, styles.unitIcon)}
                alt=""
                src={imageUrlBuilder.image(unit.image).url()!}
              />
            )}
            <div className={styles.header}>
              <Text variant="h1">{title}</Text>
              <Text className={styles.type} variant="default">
                <FormattedMessage
                  id={
                    lesson
                      ? textIdsForUnit(unit).lesson
                      : `unitType-${unit.unitType}`
                  }
                />
                {lesson && (
                  <>
                    {" "}
                    {lessonIndex + 1} of {unit.contents.length}
                  </>
                )}
              </Text>
            </div>
            <UnitOfWorkSummary
              className={styles.unitOfWorkSummary}
              unit={unit}
              lesson={lesson}
              callToAction={
                nextLessonUrl && (
                  <AnchorButton primary to={nextLessonUrl}>
                    {!lesson ? (
                      <>
                        <FormattedMessage id={textIdsForUnit(unit).lesson} />
                        &nbsp;1 details
                      </>
                    ) : unit.unitType === "designChallenge" ? (
                      "Next activity"
                    ) : (
                      "Next lesson"
                    )}
                  </AnchorButton>
                )
              }
            />
          </Card>
        </Col>
      </Row>
      <Row justify="center" className={styles.details}>
        <Col md={10} sm={12}>
          {details}
        </Col>
      </Row>
      <RelatedUnitLessonContent relatedContent={unit.related} />
    </Wrapper>
  );
};

export default UnitOfWorkLayout;
